/**
 * /activate-account — public activation status page.
 *
 * Two entry points:
 *   1. SubscribeNew.jsx routes here (with ?ref=<opaque>) immediately
 *      after opening Square checkout in a new tab. This tab polls the
 *      backend for verified payment status.
 *   2. Marketing "Activate your practitioner account" link (no ?ref)
 *      — shows the resend-activation-link form.
 *
 * The URL never grants access. `payment=complete` in the query is
 * treated as a hint only; the backend + verified Square webhook are
 * the sole authority for account activation.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, CheckCircle2, Mail, ExternalLink, AlertTriangle,
  ShieldCheck, ArrowRight,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_INTERVAL_MS = 5000;
const POLL_MAX_MS = 10 * 60 * 1000; // 10 minutes

export default function ActivateAccount() {
  const [params] = useSearchParams();
  const signupRef = (params.get("ref") || "").trim();

  // Phases: "pending_payment" | "payment_confirmed" | "already_activated"
  //         | "failed" | "unknown" | "resend"
  const [phase, setPhase] = useState(signupRef ? "pending_payment" : "resend");
  const [invoiceUrl, setInvoiceUrl] = useState("");
  const [activationUrl, setActivationUrl] = useState("");
  const [pollStartAt] = useState(() => Date.now());
  const [resendEmail, setResendEmail] = useState("");
  const [resendBusy, setResendBusy] = useState(false);
  const [resendDone, setResendDone] = useState(false);
  const [error, setError] = useState("");
  const pollTimerRef = useRef(null);

  const timedOut = useMemo(() => {
    return signupRef && phase === "pending_payment"
        && (Date.now() - pollStartAt > POLL_MAX_MS);
  }, [signupRef, phase, pollStartAt]);

  useEffect(() => {
    if (!signupRef) return;
    let cancelled = false;

    const poll = async () => {
      try {
        const r = await axios.post(`${API}/billing/activation/status`,
                                     { signup_ref: signupRef });
        const data = r.data || {};
        if (cancelled) return;
        setPhase(data.state || "unknown");
        setInvoiceUrl(data.invoice_url || "");
        setActivationUrl(data.activation_url || "");
      } catch (err) {
        if (cancelled) return;
        setError("Could not check activation status. We'll keep trying.");
      }
    };

    poll();
    pollTimerRef.current = setInterval(() => {
      if (Date.now() - pollStartAt > POLL_MAX_MS) {
        clearInterval(pollTimerRef.current);
        return;
      }
      poll();
    }, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
  }, [signupRef, pollStartAt]);

  const submitResend = async (e) => {
    e.preventDefault();
    if (!resendEmail.trim()) {
      toast.error("Please enter your email.");
      return;
    }
    setResendBusy(true);
    try {
      await axios.post(`${API}/billing/activation/resend`,
                        { email: resendEmail.trim().toLowerCase() });
      setResendDone(true);
    } catch (err) {
      // Backend is neutral — but on transport error, keep it neutral too
      setResendDone(true);
    } finally {
      setResendBusy(false);
    }
  };

  const openSquareTab = () => {
    if (invoiceUrl) window.open(invoiceUrl, "_blank", "noopener");
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-12"
          data-testid="activate-account-page">
      <div className="max-w-lg w-full bg-white shadow-xl rounded-2xl p-8"
            style={{ borderTop: "4px solid #4A1D5E" }}>
        <div className="text-xs uppercase tracking-widest font-bold mb-2"
              style={{ color: "#4A1D5E" }}>
          KCIS · Practitioner Activation
        </div>

        {/* PENDING PAYMENT */}
        {phase === "pending_payment" && !timedOut && (
          <div data-testid="activate-pending">
            <div className="flex items-center gap-3 mb-4">
              <Loader2 className="animate-spin text-purple-800" size={22} />
              <h1 className="text-2xl font-serif">Confirming your payment…</h1>
            </div>
            <p className="text-slate-700 leading-relaxed mb-4">
              We opened your Square-hosted secure checkout in a new browser tab.
              Complete payment there — this page will confirm automatically the
              moment Square notifies us.
            </p>
            {invoiceUrl && (
              <button
                onClick={openSquareTab}
                data-testid="activate-open-square"
                className="w-full py-3 rounded-lg font-semibold text-white flex items-center justify-center gap-2 mb-3"
                style={{ background: "#4A1D5E" }}>
                <ExternalLink size={18} /> Open Secure Square Checkout
              </button>
            )}
            <p className="text-xs text-slate-500 leading-relaxed">
              Your account is not activated until payment is verified by our
              server. You do not need to keep this tab open — we've also
              emailed you the activation link as a backup.
            </p>
            {error && (
              <p className="text-xs text-amber-700 mt-3">{error}</p>
            )}
          </div>
        )}

        {/* TIMEOUT — polling stopped */}
        {timedOut && (
          <div data-testid="activate-timeout">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle className="text-amber-600" size={22} />
              <h1 className="text-2xl font-serif">Still waiting on Square…</h1>
            </div>
            <p className="text-slate-700 leading-relaxed mb-4">
              Payment confirmation is taking longer than expected. Your
              subscription may still complete — Square will email you and we
              will email your activation link the moment it does.
            </p>
            <Link to="/admin/login"
                  className="inline-flex items-center gap-2 text-purple-800 font-semibold">
              Go to sign in <ArrowRight size={16} />
            </Link>
          </div>
        )}

        {/* PAYMENT CONFIRMED */}
        {phase === "payment_confirmed" && (
          <div data-testid="activate-confirmed">
            <div className="flex items-center gap-3 mb-4">
              <CheckCircle2 className="text-emerald-600" size={22} />
              <h1 className="text-2xl font-serif">Payment confirmed</h1>
            </div>
            <p className="text-slate-700 leading-relaxed mb-4">
              We've emailed you a secure activation link to finish setting up
              your practitioner account. Open your email and click "Activate
              my account" to set your password and profile.
            </p>
            <p className="text-xs text-slate-500 mb-4">
              For your security the activation link is only sent by email —
              it is never included in this page's URL.
            </p>
            <a href={activationUrl || "/admin/login"}
               className="inline-flex items-center gap-2 text-purple-800 font-semibold">
              Already activated? Sign in <ArrowRight size={16} />
            </a>
          </div>
        )}

        {/* ALREADY ACTIVATED */}
        {phase === "already_activated" && (
          <div data-testid="activate-already">
            <div className="flex items-center gap-3 mb-4">
              <ShieldCheck className="text-emerald-600" size={22} />
              <h1 className="text-2xl font-serif">Your account is ready</h1>
            </div>
            <p className="text-slate-700 leading-relaxed mb-4">
              This practitioner account has already been activated. Please
              sign in to continue.
            </p>
            <Link to="/admin/login"
                  className="inline-block w-full py-3 rounded-lg font-semibold text-white text-center"
                  style={{ background: "#4A1D5E" }}
                  data-testid="activate-goto-login">
              Sign in
            </Link>
          </div>
        )}

        {/* FAILED */}
        {phase === "failed" && (
          <div data-testid="activate-failed">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle className="text-red-600" size={22} />
              <h1 className="text-2xl font-serif">Payment could not start</h1>
            </div>
            <p className="text-slate-700 leading-relaxed mb-4">
              We were unable to start payment for this signup. Please try
              again — your card was not charged.
            </p>
            <Link to="/subscribe/new"
                  className="inline-block w-full py-3 rounded-lg font-semibold text-white text-center"
                  style={{ background: "#4A1D5E" }}>
              Start over
            </Link>
          </div>
        )}

        {/* RESEND (no ref present) or UNKNOWN */}
        {(phase === "resend" || phase === "unknown") && (
          <div data-testid="activate-resend">
            <h1 className="text-2xl font-serif mb-2">
              Activate your practitioner account
            </h1>
            <p className="text-slate-700 leading-relaxed mb-4">
              Already paid but didn't receive the activation email? Enter the
              email you used at checkout — we'll resend the secure link.
            </p>
            {!resendDone ? (
              <form onSubmit={submitResend} className="space-y-3">
                <label className="text-xs font-semibold text-slate-600 block">
                  Email address
                </label>
                <input
                  type="email" required value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:outline-none focus:border-purple-700"
                  data-testid="activate-resend-email" />
                <button type="submit" disabled={resendBusy}
                        data-testid="activate-resend-submit"
                        className="w-full py-3 rounded-lg font-semibold text-white flex items-center justify-center gap-2 disabled:opacity-50"
                        style={{ background: "#4A1D5E" }}>
                  {resendBusy ? (<Loader2 className="animate-spin" size={18} />) : (<Mail size={18} />)}
                  {resendBusy ? "Sending…" : "Resend activation link"}
                </button>
                <p className="text-xs text-slate-500">
                  For your privacy we return the same response whether or not
                  an account exists.
                </p>
              </form>
            ) : (
              <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-4 text-sm text-emerald-800"
                    data-testid="activate-resend-done">
                If a paid activation is pending for that email, we've resent
                the secure activation link. Please check your inbox.
              </div>
            )}
            <div className="mt-6 flex items-center justify-between text-sm">
              <Link to="/admin/login" className="text-purple-800 font-semibold"
                    data-testid="activate-goto-signin">
                Sign in
              </Link>
              <Link to="/admin/forgot-password" className="text-slate-600">
                Forgot password?
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
